import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Scissors, Loader2, Radio, CheckCircle2, Film, ExternalLink, Volume2, VolumeX } from "lucide-react";
import { API, PUBLIC_ORIGIN } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useClipKey } from "@/components/QuickClipAccess";
import { feedback, soundOn, setSoundOn } from "@/lib/feedback";
import { clipErr } from "@/lib/clipErrors";
import { QRCodeSVG } from "qrcode.react";

const PanelQR = ({ url, t }) => (
  <div className="cx-card rounded-2xl p-4 mb-6 flex items-center gap-4" data-testid="panel-qr">
    <div className="bg-white p-2 rounded-lg shrink-0" data-testid="panel-qr-code"><QRCodeSVG value={url} size={104} /></div>
    <div className="min-w-0">
      <div className="text-sm font-semibold text-white">{t("pn.qr_title")}</div>
      <p className="text-[11px] text-slate-400 mt-1">{t("pn.qr_desc")}</p>
    </div>
  </div>
);

const SoundToggle = ({ t }) => {
  const [on, setOn] = useState(soundOn());
  const flip = () => { setSoundOn(!on); setOn(!on); if (!on) setTimeout(() => feedback("ready"), 0); };
  return (
    <button onClick={flip} data-testid="panel-sound-toggle" aria-pressed={on} title={t(on ? "pn.sound_off" : "pn.sound_on")}
      className="cx-metal rounded-full p-2">{on ? <Volume2 className="w-4 h-4 text-[#22e3a1]" /> : <VolumeX className="w-4 h-4 text-slate-500" />}</button>
  );
};

const ClipRow = ({ c, t }) => (
  <div className="flex items-center gap-3 rounded-xl bg-white/[0.03] border border-white/5 px-3 py-2.5" data-testid={`panel-clip-${c.id}`}>
    {c.status === "done" ? <CheckCircle2 className="w-4 h-4 text-[#22e3a1] shrink-0" /> : c.status === "error" ? <Film className="w-4 h-4 text-rose-400 shrink-0" /> : <Loader2 className="w-4 h-4 text-[#3ad7ff] animate-spin shrink-0" />}
    <div className="min-w-0 flex-1">
      <div className="text-sm text-white truncate">{c.title}</div>
      <div className="text-[10px] font-mono uppercase text-slate-500">{c.manual ? t("pn.manual") : t("pn.auto")} · {t(`pn.st_${c.status}`)}</div>
    </div>
    {c.score != null && <span className="font-mono text-sm text-[#3ad7ff]">{c.score}</span>}
  </div>
);

export default function LivePanel() {
  const { key } = useParams();
  const [sp] = useSearchParams();
  const dock = sp.get("dock") === "1";
  const { t } = useI18n();
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const seen = useRef(null);

  const load = useCallback(() => fetch(`${API}/panel/${key}${dock ? "?dock=1" : ""}`).then(async (r) => {
    if (!r.ok) { setSt({ invalid: true }); return; }
    const d = await r.json();
    const done = d.clips.filter((c) => c.status === "done");
    if (seen.current) done.filter((c) => !seen.current.has(c.id)).forEach((c) => { toast.success(`${t("pn.ready")} ${c.title}`); feedback("ready"); });
    seen.current = new Set(done.map((c) => c.id));
    setSt(d);
  }).catch(() => {}), [key, t, dock]);

  useEffect(() => { load(); const i = setInterval(load, 3000); return () => clearInterval(i); }, [load]);

  const clipNow = useCallback(async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/panel/${key}/clip-now`, { method: "POST" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail);
      feedback("launch");
      toast.success(t("pn.requested"));
      load();
    } catch (e) { feedback("error"); toast.error(clipErr(e.message, t)); } finally { setBusy(false); }
  }, [key, load, t]);
  useClipKey(clipNow, !busy && !!st?.live);

  if (st?.invalid) return <div className="min-h-screen grid place-items-center text-slate-400 text-sm" data-testid="panel-invalid">{t("pn.invalid")}</div>;
  const live = st?.live;
  return (
    <div className={`min-h-screen bg-[#05060d] ${dock ? "p-2" : "p-4 sm:p-6"} max-w-xl mx-auto`} data-testid="live-panel" data-dock={dock ? "on" : "off"}>
      <div className={`flex items-center gap-3 ${dock ? "mb-2" : "mb-5"}`}>
        <img src="/logo-x.png" alt="" className="w-10 h-8 object-contain" />
        <div className="flex-1">
          <div className="font-display font-bold text-white">{t("pn.title")}</div>
          {!dock && <div className="text-[11px] text-slate-500">{t("pn.private")}</div>}
        </div>
        <SoundToggle t={t} />
        <a href="/clips" target="_blank" rel="noreferrer" className="cx-metal rounded-full px-3 py-1.5 text-xs flex items-center gap-1.5" data-testid="panel-open-clips"><ExternalLink className="w-3.5 h-3.5" />{t("pn.open")}</a>
      </div>
      <div className="cx-card rounded-2xl p-4 mb-4" data-testid="panel-live-status">
        <div className="flex items-center gap-2 text-sm">
          <Radio className={`w-4 h-4 ${live ? "text-rose-500 animate-pulse" : "text-slate-600"}`} />
          <span className="text-white font-semibold">{live ? t("pn.live_on") : t("pn.live_off")}</span>
          {live && <span className="ml-auto text-[11px] font-mono text-slate-400" data-testid="panel-buffer">{t("pn.buffer", { s: live.buffer })}</span>}
        </div>
        {live && <div className="text-[11px] text-slate-500 mt-1 truncate">{live.url} · {t("pn.stats", { s: live.segments, c: live.clips })}</div>}
        {!live && <p className="text-xs text-slate-400 mt-2">{t("pn.live_off_how")}</p>}
        {(live?.last_error || (!live && st?.last_session?.status === "error")) && <p className="text-xs text-rose-400 mt-2" data-testid="panel-error">{live?.last_error || st.last_session.last_error}</p>}
      </div>
      <button onClick={clipNow} disabled={busy || !live} data-testid="panel-clip-now-btn"
        className="cx-btn w-full rounded-2xl py-6 text-lg font-display font-bold flex items-center justify-center gap-3 disabled:opacity-40">
        {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <Scissors className="w-6 h-6" />} {t("pn.clip_now")}
        <kbd className="hidden sm:inline text-xs font-mono px-2 py-0.5 rounded bg-black/30 border border-white/30">C</kbd>
      </button>
      <p className="text-[11px] text-slate-500 text-center mt-2 mb-6">{t("pn.clip_now_d")}{live?.chat_cmd ? <span className="block text-[#22e3a1] mt-1" data-testid="panel-chat-cmd">{t("pn.chat_on")}</span> : null}</p>
      {!dock && <PanelQR url={`${PUBLIC_ORIGIN}/panel/${key}`} t={t} />}
      <div className="cx-label mb-2">{t("pn.recent")}</div>
      <div className="space-y-2" data-testid="panel-clips">
        {st?.clips?.length ? st.clips.slice(0, dock ? 4 : 12).map((c) => <ClipRow key={c.id} c={c} t={t} />) : <div className="text-xs text-slate-500">{t("pn.none")}</div>}
      </div>
    </div>
  );
}
